/** 
 * @namespace Modal
 * @memberof Promega.Components
 * @description Methods for showing and hiding modal dialogs. Some functionality is automatic: see examples for more details.
 * 
 * @example <caption>If an element with class "trigger--modal-dialog" is clicked and the element's next sibling has class "modal-dialog", that dialog will be automatically shown without the need to write additional JavaScript.</caption>
 * <button type="button">Show a modal</button>
 * <div class="modal-dialog">This is a modal dialog.</div>
 * 
 * @example <caption>By default, a modal dialog does not contain any padding. This makes it easier to show specialized content inside the modal. For general content, include an inner element with class "modal-dialog__content" to add the standard padding.</caption>
 * <div class="modal-dialog">
 *   <div class="modal-dialog__content">This is a modal dialog, with standard padding.</div>
 * </div>
 * 
 * @example <caption>A modal dialog may also contain a title bar with custom text, as well as a close button.</caption>
 * <div class="modal-dialog">
 *   <header class="modal-dialog__header">
 *     <button type="button" class="modal-dialog__close-button">Close</button>
 *     <span>Title text for the modal</span>
 *  </header>
 *  <div class="modal-dialog__content">Content goes here.</div>
 * </div>
 */

import throttle from '../util/throttle';
import isAnimationSupported from '../util/isAnimationSupported';
import trackEvent from '../util/trackEvent';
import { v4 as uuid } from 'uuid';

const TRIGGER_CLASS = "trigger--modal-dialog",
    MODAL_DIALOG_CLASS = "modal-dialog",
    MODAL_PLACEHOLDER_CLASS = "modal-placeholder",
    MODAL_DIALOG_ENTERING_CLASS = "modal-dialog--entering",
    MODAL_DIALOG_VISIBLE_CLASS = "modal-dialog--visible",
    CLICKEATER_CLASS = "modal-dialog-clickeater",
    CLICKEATER_ENTERING_CLASS = "modal-dialog-clickeater--entering",
    CLICKEATER_EXITING_CLASS = "modal-dialog-clickeater--exiting",
    MODAL_DIALOG_CLOSE_BUTTON = "modal-dialog__close-button",
    MODAL_EDGE_SPACING = 16;

let modalIsVisible = false,
    modalIsLocked = false,
    allowCloseButtonLockedDismiss = false,
    isModalAnimating = false,
    isClickeaterAnimating = false,
    afterHide = {},
    autoHidden = false,
    promiseResolveShow,
    promiseResolveHide;

// Private functions/listeners

function clickedModalDialogTrigger (event) {
    if (event.target && event.target.classList.contains(TRIGGER_CLASS)) {
        // Clicked a modal trigger.
        const nextSibling = event.target.nextElementSibling;
        if (nextSibling && nextSibling.classList.contains(MODAL_DIALOG_CLASS)) {
            event.preventDefault();
            event.stopPropagation();
            event.target.blur();
            show(nextSibling, event.target, { hideOnScroll: true, hideOnResize: true }).then(() => {
                trackEvent("Components", "Modal - opened", "Trigger element");
            }).catch(() => { });
            
        }
    }
    else if (event.target && event.target.classList.contains(MODAL_DIALOG_CLOSE_BUTTON)) {
        if (modalIsLocked && allowCloseButtonLockedDismiss)
            unlock();
        hide().then(() => { trackEvent("Components", "Modal - closed", "Close button") }).catch(() => { });
    }
}

function popstateHandler(event) {
    if (event.state === null && isVisible()) {
        // Base state. Clear the modal.
        hide({ skipHistoryUpdate: true }).then(() => { trackEvent("Components", "Modal - closed", "Browser history") });
    }
    else if (event.state && event.state.type === "modal") {
        const existingModal = document.querySelector(`.${MODAL_DIALOG_VISIBLE_CLASS}[data-modal-id='${event.state.data.modalId}']`);
        if (existingModal) {
            if (existingModal.getAttribute("data-modal-id") === getTopModal().getAttribute("data-modal-id")) {
                // Topmost modal in DOM does not match the current state. Do nothing.
            }
            else {
                // Topmost modal in DOM does not match the current state. Hide the topmost modal.
                hide({ skipHistoryUpdate: true }).then(() => { trackEvent("Components", "Modal - closed", "Browser history") });
            }
        }
        else {
            // Modal is not on the page. Show it anew.
            const modalToShow = document.querySelector(`.${MODAL_DIALOG_CLASS}[data-modal-id='${event.state.data.modalId}']`);
            if (modalToShow) {
                const anchorToUse = document.querySelector(`*[data-modal-id='${event.state.data.modalId}']:not(.${MODAL_DIALOG_CLASS})`);
                const afterHideFunction = afterHide.hasOwnProperty(event.state.data.modalId) ? afterHide[event.state.data.modalId] : null;
                show(modalToShow, anchorToUse, Object.assign({}, event.state.data.options, { omitFromHistory: true, afterHide: afterHideFunction }))
                    .then(() => { trackEvent("Components", "Modal - opened", "Browser history") });
            }
        }
    }
}

const autoHideHandler = throttle(event => {
    if (modalIsVisible && !autoHidden && !isModalAnimating && !isClickeaterAnimating) {
        autoHidden = true;
        hide().then(() => {
            trackEvent("Components", "Modal - closed", "Scrolling")
        }).catch(() => { });
    }
}, 100);

function hideModalOnEsc (event) {
    if (event.keyCode === 27) {
        event.stopPropagation();
        hide().then(() => { trackEvent("Components", "Modal - closed", "Esc key") }).catch(() => { });
    }
}

function clickeaterAnimationEnded (event) {
    isClickeaterAnimating = false;
    isModalAnimating = false;
    const modal = event.target.querySelector(`.${MODAL_DIALOG_CLASS}`);
    if (modal) {
        const modalId = modal.getAttribute("data-modal-id");
        if (event.target.classList.contains(CLICKEATER_ENTERING_CLASS)) {
            event.target.classList.remove(CLICKEATER_ENTERING_CLASS);
            modal.classList.remove(MODAL_DIALOG_ENTERING_CLASS);
            event.target.removeEventListener("animationend", clickeaterAnimationEnded);
        }
        else if (event.target.classList.contains(CLICKEATER_EXITING_CLASS)) {
            cleanupModalAfterHide(modal);

            if (afterHide[modalId]) {
                try {
                    afterHide[modalId]();
                }
                catch (afterHideErr) { }
                afterHide[modalId] = null;
            }

            // Move the modal back to its original location
            const modalPlaceholder = document.querySelector(`.${MODAL_PLACEHOLDER_CLASS}[data-modal-id='${modalId}']`);
            if (modalPlaceholder) {
                modalPlaceholder.parentElement.insertBefore(modal.parentElement.removeChild(modal), modalPlaceholder);
                modalPlaceholder.parentElement.removeChild(modalPlaceholder);
            }
            event.target.parentElement.removeChild(event.target);
        }
    }
    if (typeof promiseResolveShow === "function") {
        promiseResolveShow();
        promiseResolveShow = null;
    }
    if (typeof promiseResolveHide === "function") {
        promiseResolveHide();
        promiseResolveHide = null;
    }
}

function cleanupModalAfterHide (modalEl) {
    modalEl.style.position = "";
    modalEl.style.left = "";
    modalEl.style.top = "";
    modalEl.style.width = "";
    modalEl.style.height = "";

    const modalCloseButton = modalEl.querySelector(`.${MODAL_DIALOG_CLOSE_BUTTON}`);
    if (modalCloseButton) modalCloseButton.removeEventListener("click", clickedModalDialogTrigger);

    let modalElement = document.querySelector(".modal-dialog-clickeater");
    modalElement.classList.remove("modal-dialog-clickeater");
}

function getTopModal() {
    const allModals = document.getElementsByClassName(MODAL_DIALOG_VISIBLE_CLASS);
    return allModals[allModals.length - 1];
}

function getTopBackdrop() {
    const allBackdrops = document.getElementsByClassName(CLICKEATER_CLASS);
    return allBackdrops[allBackdrops.length - 1];
}

// API

/**
 * @function show
 * @memberof Promega.Components.Modal
 * @description Shows the given modal element and anchors it to the provided element
 * @param {HTMLElement} modalEl The modal dialog to show
 * @param {HTMLElement} [anchorEl] An arbitrary HTML element near which to position the modal dialog
 * @param {JSON} [options]
 * @param {Function} options.afterHide A function to call after the dialog has been hidden
 * @param {Boolean} options.hideOnScroll If true, the modal automatically hides when the user scrolls the page
 * @param {Boolean} options.hideOnResize If true, the modal automatically hides when the user resizes the browser window
 * @param {Number} options.width The desired width at which to show the modal dialog
 * @param {Number} options.height The desired height at which to show the modal dialog
 */
export function show(modalEl, anchorEl, options) {
    return new Promise((resolve, reject) => {
        if (modalEl && !isModalAnimating && !isClickeaterAnimating && !modalIsLocked) {
            promiseResolveShow = resolve;
            // Set the visibility status and show the modal
            modalIsVisible = true;
            autoHidden = false;

            let candidateLeft = 0,
                candidateTop = 0,
                modalWidth,
                modalHeight;

            if (options) {
                if (options.hideOnScroll) document.addEventListener("scroll", autoHideHandler);
                if (options.hideOnResize) window.addEventListener("resize", autoHideHandler);
                if (options.width != null) modalWidth = parseInt(options.width, 10);
                if (options.height != null) modalHeight = parseInt(options.height, 10);
            }

            const modalCloseButton = modalEl.querySelector(`.${MODAL_DIALOG_CLOSE_BUTTON}`);
            if (modalCloseButton) modalCloseButton.addEventListener("click", clickedModalDialogTrigger);

            // Container setup
            const clickeaterEl = document.createElement("div");
            clickeaterEl.className = CLICKEATER_CLASS;
            clickeaterEl.addEventListener("click", (event) => {
                if (event.target.classList.contains(CLICKEATER_CLASS))
                    hide().then(() => { trackEvent("Components", "Modal - closed", "Backdrop click") }).catch(() => { });
            });

            // Reuse the modal's UUID if it exists; otherwise, generate a new one
            const modalId = modalEl.hasAttribute("data-modal-id") ? modalEl.getAttribute("data-modal-id") : uuid(),
                modalPlaceholder = document.createElement("div");
            modalPlaceholder.setAttribute("data-modal-id", modalId);
            modalPlaceholder.className = MODAL_PLACEHOLDER_CLASS;
            modalEl.setAttribute("data-modal-id", modalId);
            modalEl.setAttribute("aria-hidden", "false");
            modalEl.parentElement.insertBefore(modalPlaceholder, modalEl);
            clickeaterEl.appendChild(modalEl.parentElement.removeChild(modalEl));

            if (anchorEl) anchorEl.setAttribute("data-modal-id", modalId);
            if (options && options.afterHide && typeof options.afterHide === "function") afterHide[modalId] = options.afterHide;

            document.body.appendChild(clickeaterEl);
            document.documentElement.addEventListener("keydown", hideModalOnEsc);

            // Compute modal sizing
            if (isNaN(modalWidth)) modalWidth = null;
            if (isNaN(modalHeight)) modalHeight = null;

            // Apply custom dimensions to the modal, if available
            if (modalWidth !== null) modalEl.style.width = `${modalWidth}px`;
            if (modalHeight != null) modalEl.style.height = `${modalHeight}px`;

            if (modalWidth === null || modalHeight === null) {
                // No custom size passed. Get dimensions from the modal itself
                modalEl.style.display = "flex";
                if (modalWidth === null) modalWidth = modalEl.offsetWidth;
                if (modalHeight === null) modalHeight = modalEl.offsetHeight;
                modalEl.style.display = "";
            }

            if (anchorEl) {
                const boundingRect = anchorEl.getBoundingClientRect();
                candidateLeft = boundingRect.left;
                candidateTop = boundingRect.top + anchorEl.offsetHeight;

                // Correct the element's position if it overflows the right or bottom of the page
                const candidateRight = candidateLeft + modalWidth,
                    windowWidth = window.innerWidth || document.documentElement.clientWidth;
                if (candidateRight > windowWidth - MODAL_EDGE_SPACING) {
                    candidateLeft = windowWidth - (modalWidth + 2 * MODAL_EDGE_SPACING);
                }

                const candidateBottom = candidateTop + modalHeight,
                    windowHeight = window.innerHeight || document.documentElement.clientHeight;
                if (candidateBottom > windowHeight - MODAL_EDGE_SPACING) {
                    candidateTop = windowHeight - (modalHeight + MODAL_EDGE_SPACING);
                }

                // Set the coordinates
                modalEl.style.position = "absolute";
                modalEl.style.left = candidateLeft + "px";
                modalEl.style.top = candidateTop + "px";
            }





            if (isAnimationSupported()) {
                isModalAnimating = true;
                isClickeaterAnimating = true;
                modalEl.classList.add(MODAL_DIALOG_ENTERING_CLASS);
                clickeaterEl.classList.add(CLICKEATER_ENTERING_CLASS);
                clickeaterEl.addEventListener("animationend", clickeaterAnimationEnded);
            }

            modalEl.classList.add(MODAL_DIALOG_VISIBLE_CLASS);
            if (document.querySelector("main")) {
                document.querySelector("main").setAttribute("aria-hidden", "true");
            }


            if (options && options.omitFromHistory) {
                history.replaceState({ type: "modal", data: { modalId, options: Object.assign({}, options, { afterHide: null }) } }, "", "");
            }
            else {
                history.pushState({ type: "modal", data: { modalId, options: Object.assign({}, options, { afterHide: null }) } }, "", "");
            }
        }
        else reject();
    });
    
}

/**
 * @function hide
 * @memberof Promega.Components.Modal
 * @description Hides the modal dialog that is currently visible, if there is one.
 */
export function hide(options) {
    return new Promise((resolve, reject) => {
        if (modalIsVisible
            && !isModalAnimating
            && !isClickeaterAnimating
            && !modalIsLocked) {
            promiseResolveHide = resolve;

            // Set the visibility status and hide the modal
            const currentModal = getTopModal(),
                currentClickeater = getTopBackdrop();
            if (currentModal) {
                currentModal.classList.remove(MODAL_DIALOG_VISIBLE_CLASS);

                if (isAnimationSupported()) {
                    isClickeaterAnimating = true;
                    if (currentClickeater) {
                        currentClickeater.classList.add(CLICKEATER_EXITING_CLASS);
                        currentClickeater.addEventListener("animationend", clickeaterAnimationEnded);
                    }
                }
                else if (currentClickeater) clickeaterAnimationEnded({ target: currentClickeater });

                if (document.getElementsByClassName(MODAL_DIALOG_VISIBLE_CLASS).length === 0) {
                    modalIsVisible = false;
                    document.documentElement.removeEventListener("keydown", hideModalOnEsc);
                    document.removeEventListener("scroll", autoHideHandler);
                    window.removeEventListener("resize", autoHideHandler);
                }

                if (!(options && options.skipHistoryUpdate)
                    && history && history.state
                    && history.state["type"] === "modal") {
                    if (history.state.data.modalId === currentModal.getAttribute("data-modal-id")) {
                        // ID matches a state. Navigate back in history.
                        history.back();
                    }
                }
            }
            if (document.querySelector("main")) {
                document.querySelector("main").setAttribute("aria-hidden", "false");
            }
        }
        else reject();
    });
}

/**
 * @function isVisible
 * @memberof Promega.Components.Modal
 * @description Returns a value indicating whether or not a modal dialog is currently visible on the page.
 * @returns {Boolean} True if there is a modal dialog visible, otherwise false.
 */
export function isVisible() {
    return modalIsVisible;
}

/**
 * @function lock
 * @memberof Promega.Components.Modal
 * @description Locks the currently active modal so it cannot be dismissed. Has no effect if a modal is not currently being shown. A modal dialog that is locked cannot be dismissed by conventional methods (calling <code>hide()</code>, clicking the close button, or clicking the backdrop).
*/
export function lock(options) {
    if (isVisible()) modalIsLocked = true;
    if (options && options["allowCloseButton"] === true) {
        allowCloseButtonLockedDismiss = true;
    }
}

/**
 * @function unlock
 * @memberof Promega.Components.Modal
 * @description Unlocks the modal so it can be dismissed.
*/
export function unlock() {
    modalIsLocked = false;
    allowCloseButtonLockedDismiss = false;
}

// Init code

if (typeof document !== "undefined") {
    document.addEventListener("click", clickedModalDialogTrigger);
    window.addEventListener("popstate", popstateHandler);
    const autoModals = document.querySelectorAll(`.${MODAL_DIALOG_CLASS}[data-autoshow]`);
    for (let i = 0; i < autoModals.length; i++) {
        show(autoModals[i]);
        trackEvent("Components", "Modal - opened", "Automatic (on page load)");
    }
}
